import { useState } from 'react';
import { useI18n } from '../../i18n/I18nProvider';
import type { Language } from '../../i18n/catalogs';
import type { RoleCode } from '../../auth/types';
import type { ClosingReport, ControlOutcome, ControlStatus } from './closing.api';
import { closeBlockedBy, controlTitle, reopenRefusal, summariseControls } from './closing';
import './closing.css';

export interface ClosingViewProps {
  report: ClosingReport;
  language: Language;
  roles: readonly RoleCode[];
  onClose(): void;
  onReopen(reason: string): void;
  busy?: boolean;
  error?: string | null;
}

const STATUS_KEY: Record<ControlStatus, string> = {
  passed: 'closingPassed',
  failed: 'closingFailed',
  skipped: 'closingSkipped',
};

function chip(status: ControlStatus): string {
  return `chip chip--${status}`;
}

export function ClosingView({
  report,
  language,
  roles,
  onClose,
  onReopen,
  busy = false,
  error = null,
}: ClosingViewProps): JSX.Element {
  const { t } = useI18n();
  const [reopenOpen, setReopenOpen] = useState(false);
  const [reason, setReason] = useState('');

  const summary = summariseControls(report.controls);
  const blocking: ControlOutcome | undefined = closeBlockedBy(report.controls);
  const blocked = blocking !== undefined || !report.canClose;
  const isAdmin = roles.includes('admin');
  const monthName = t(`closingMonth${report.period.month}`);
  const periodLabel = `${monthName} ${report.period.year}`;
  const refusal = reopenRefusal(roles, reason);

  const submitReopen = (): void => {
    if (refusal !== null) {
      return;
    }
    onReopen(reason.trim());
    setReopenOpen(false);
    setReason('');
  };

  return (
    <section className={`closing${blocked ? ' closing--blocked' : ''}`} data-state={blocked ? 'blocked' : 'ready'}>
      <header className="page-head closing__head">
        <div>
          <h1>{t('closingTitle')}</h1>
          <p className="muted">
            {periodLabel} · {t(`closingStatus_${report.period.status}`)}
          </p>
        </div>
        <div className="closing__actions">
          {isAdmin ? (
            <button
              type="button"
              className="btn btn--ghost"
              data-testid="reopen-period"
              onClick={() => setReopenOpen(true)}
              disabled={busy || report.period.status !== 'closed'}
            >
              {t('closingReopen')}
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn--primary"
            data-testid="close-period"
            disabled={blocked || busy}
            onClick={onClose}
          >
            {t('closingClose')}
          </button>
        </div>
      </header>

      <div
        className={`closing__banner ${blocked ? 'closing__banner--bad' : 'closing__banner--ok'}`}
        data-testid="closing-banner"
      >
        {blocked ? (
          <p>
            <strong>{t('closingBlocked')}</strong>
            {blocking === undefined ? null : (
              <>
                {' — '}
                {blocking.id}: {blocking.message}
              </>
            )}
          </p>
        ) : (
          <p>
            <strong>{t('closingReady')}</strong>
          </p>
        )}
      </div>

      <div className="closing__summary">
        <article className="metric" data-testid="closing-passed">
          <span className="metric__label">{t('closingPassed')}</span>
          <span className="metric__value">{summary.passed}</span>
        </article>
        <article className="metric" data-testid="closing-failed">
          <span className="metric__label">{t('closingFailed')}</span>
          <span className="metric__value">{summary.failed}</span>
        </article>
        <article className="metric" data-testid="closing-skipped">
          <span className="metric__label">{t('closingSkipped')}</span>
          <span className="metric__value">{summary.skipped}</span>
        </article>
        <article className="metric" data-testid="closing-total">
          <span className="metric__label">{t('closingTotal')}</span>
          <span className="metric__value">{summary.total}</span>
        </article>
      </div>

      {error === null ? null : <p className="closing__error">{error}</p>}

      <table className="closing__controls" data-testid="closing-controls">
        <thead>
          <tr>
            <th scope="col">{t('closingNumber')}</th>
            <th scope="col">{t('closingControl')}</th>
            <th scope="col">{t('closingOwner')}</th>
            <th scope="col">{t('closingResult')}</th>
            <th scope="col">{t('closingMessage')}</th>
          </tr>
        </thead>
        <tbody>
          {report.controls.map((control) => (
            <tr key={control.id} className={`closing__row closing__row--${control.status}`}>
              <td className="closing__id">{control.id}</td>
              <td>{controlTitle(control.title, language)}</td>
              <td className="closing__owner">{control.owner}</td>
              <td>
                <span className={chip(control.status)} data-testid={`chip-${control.id}`}>
                  {t(STATUS_KEY[control.status])}
                </span>
              </td>
              <td className="closing__message">{control.message}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {reopenOpen ? (
        <div className="closing__modal" role="dialog" aria-modal="true" aria-label={t('closingReopen')}>
          <div className="closing__modal-body">
            <header className="closing__modal-head">
              <h3>{t('closingReopenTitle')}</h3>
              <button
                type="button"
                className="btn btn--ghost"
                aria-label={t('closingCancel')}
                onClick={() => setReopenOpen(false)}
              >
                ×
              </button>
            </header>
            <p className="muted">{t('closingReopenHint')}</p>
            <label className="closing__field">
              <span>{t('closingReason')}</span>
              <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} />
            </label>
            {refusal === null ? null : <p className="closing__error">{t(refusal)}</p>}
            <footer className="closing__modal-foot">
              <button type="button" className="btn btn--ghost" onClick={() => setReopenOpen(false)}>
                {t('closingCancel')}
              </button>
              <button
                type="button"
                className="btn btn--primary"
                data-testid="reopen-confirm"
                disabled={refusal !== null || busy}
                onClick={submitReopen}
              >
                {t('closingReopenConfirm')}
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </section>
  );
}
